/* eslint-disable */
/**
 * MRRKurveBIPanel.jsx - MRR-kurve med maal-linjer
 * Endpoint: GET /api/os/bi/mrr-kurve
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

function fmtKr(val) {
  if (val == null || isNaN(val)) return '—';
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(val);
}

function fmtMaaned(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('da-DK', { month: 'short', year: '2-digit' });
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      background: 'rgba(8,12,30,0.95)', border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 8, padding: '10px 14px', fontSize: 13,
    }}>
      <div style={{ color: '#7a8fa6', marginBottom: 6, fontWeight: 600 }}>{fmtMaaned(label)}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, marginBottom: 3 }}>
          {p.name}: <strong>{fmtKr(p.value)}</strong>
        </div>
      ))}
    </div>
  );
};

export default function MRRKurveBIPanel() {
  const [data, setData] = useState(null);
  const [fejl, setFejl] = useState(null);
  const [loader, setLoader] = useState(true);

  const hent = useCallback(async () => {
    try {
      const r = await fetch('/api/os/bi/mrr-kurve', { credentials: 'include' });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const d = await r.json();
      setData(d);
      setFejl(null);
    } catch (err) {
      setFejl(err.message);
    } finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => { hent(); }, [hent]);

  const chartData = data && data.data ? data.data.map(r => ({
    maaned: r.maaned,
    mrr: r.mrr,
    realistisk: data.maal_realistisk,
    ambitioes: data.maal_ambitioes,
  })) : [];

  const aktuel = data ? (data.aktuel_mrr || 0) : 0;
  const maalR = data ? (data.maal_realistisk || 0) : 0;
  const maalA = data ? (data.maal_ambitioes || 0) : 0;
  const gap = maalR - aktuel;
  const pct = maalR > 0 ? Math.round((aktuel / maalR) * 100) : 0;

  return (
    <div style={{ width: '100%' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        flexWrap: 'wrap', gap: 12, marginBottom: 20,
      }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>MRR-kurve</div>
          <div style={{ fontSize: 13, color: '#7a8fa6', marginTop: 3 }}>
            Faktisk MRR vs. realistisk og ambitiost maal (maaned)
          </div>
        </div>
        {data && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{
              background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)',
              borderRadius: 8, padding: '8px 14px', minWidth: 140,
            }}>
              <div style={{ fontSize: 11, color: '#7a8fa6', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Aktuel MRR</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#3b82f6' }}>{fmtKr(aktuel)}</div>
            </div>
            <div style={{
              background: gap > 0 ? 'rgba(245,158,11,0.12)' : 'rgba(34,197,94,0.12)',
              border: '1px solid ' + (gap > 0 ? 'rgba(245,158,11,0.3)' : 'rgba(34,197,94,0.3)'),
              borderRadius: 8, padding: '8px 14px', minWidth: 140,
            }}>
              <div style={{ fontSize: 11, color: '#7a8fa6', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Gap til realistisk</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: gap > 0 ? '#f59e0b' : '#22c55e' }}>
                {gap > 0 ? '-' : '+'}{fmtKr(Math.abs(gap))}
              </div>
              <div style={{ fontSize: 11, color: '#7a8fa6' }}>{pct}% af maalet</div>
            </div>
          </div>
        )}
      </div>

      {loader && <div style={{ textAlign: 'center', padding: 40, color: '#7a8fa6' }}>Henter...</div>}
      {fejl && !loader && (
        <div style={{
          background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 8, padding: '12px 16px', color: '#f87171', fontSize: 13,
        }}>Fejl: {fejl}</div>
      )}
      {!loader && !fejl && chartData.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#7a8fa6', fontSize: 13 }}>
          Ingen MRR-data endnu
        </div>
      )}
      {!loader && !fejl && chartData.length > 0 && (
        <div style={{ width: '100%', overflowX: 'auto' }}>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="maaned"
                tickFormatter={fmtMaaned}
                tick={{ fill: '#7a8fa6', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                tickLine={false}
              />
              <YAxis
                tickFormatter={function(v) { return Math.round(v / 1000) + 'k'; }}
                tick={{ fill: '#7a8fa6', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={48}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 12, color: '#7a8fa6', paddingTop: 8 }}
                formatter={function(v) { return ({ mrr: 'Faktisk MRR', realistisk: 'Maal realistisk', ambitioes: 'Maal ambitiost' })[v] || v; }}
              />
              <Bar dataKey="mrr" name="mrr" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={40} />
              <Line type="monotone" dataKey="realistisk" name="realistisk" stroke="#22c55e" strokeWidth={2} strokeDasharray="6 3" dot={false} />
              <Line type="monotone" dataKey="ambitioes" name="ambitioes" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="4 4" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      <div style={{ marginTop: 14, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11, color: '#7a8fa6' }}>
        <span><span style={{ color: '#22c55e' }}>---</span> Realistisk: {fmtKr(maalR)}/md (3,5M/aar)</span>
        <span><span style={{ color: '#8b5cf6' }}>---</span> Ambitiost: {fmtKr(maalA)}/md (5M/aar)</span>
      </div>
    </div>
  );
}
